import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface CalendarEntry {
  readonly entryType: string;
  readonly refType: 'RELEASE' | 'ENVIRONMENT';
  readonly refId: string;
  readonly code: string;
  readonly name: string;
  readonly environmentId: string | null;
  readonly start: string | null;
  readonly end: string | null;
}

const ENTRY_TYPES = ['', 'PLANNED_RELEASE', 'SCHEDULED_RELEASE', 'EMERGENCY_RELEASE', 'FREEZE_WINDOW'];

/**
 * M32 — Release Calendar (§31 / §14). Unified forward schedule of planned,
 * scheduled and emergency releases (T422) overlaid with environment freeze
 * windows (T428) so change managers can spot collisions before authorising a
 * deployment. Filterable by entry type; each row shows the scheduled window.
 */
export function ReleaseCalendarPage(): ReactNode {
  const { at } = useBusinessDay();
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<CalendarEntry[]>([]);
  const [entryType, setEntryType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (entryType) params.set('entryType', entryType);
    const qs = params.toString();
    apiRequest<{ data: CalendarEntry[] }>(`/v1/devops/calendar${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, entryType]);

  return (
    <main aria-label="release-calendar">
      <h1>{tr('m32.calendar.title')}</h1>
      <div className="filters">
        <label>
          {tr('m32.calendar.entryType')}
          <select aria-label="filter-entry-type" value={entryType} onChange={(e) => setEntryType(e.target.value)}>
            {ENTRY_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.calendar.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.calendar.loading')}</p> : (
        <>
          <p aria-label="entry-count">{tr('m32.calendar.count', { count: rows.length })}</p>
          <table aria-label="calendar-table">
            <thead><tr><th>{tr('m32.calendar.thType')}</th><th>{tr('m32.calendar.thRef')}</th><th>{tr('m32.calendar.thCode')}</th><th>{tr('m32.calendar.thName')}</th><th>{tr('m32.calendar.thStart')}</th><th>{tr('m32.calendar.thEnd')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.refType}-${r.refId}`} data-entry-type={r.entryType}>
                  <td>{r.entryType}</td><td>{r.refType}</td><td>{r.code}</td><td>{r.name}</td>
                  <td>{at(r.start)}</td><td>{at(r.end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
